/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: photov2 专用批量去水印按钮组件
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-28 10:13:15
 */
import React, { useMemo, useRef, useState } from 'react'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Button, Modal, message } from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  useSensorsRole
} from '../../../../app/hooks'
import {
  batchRemoveWatermarkCheck,
  createProducePhotoTask
} from '../../../../actions/photo'
import RemoveWatermarkModal from '../../../components/RemoveWatermarkModal'
import SelectPhotoTip from '../../../components/SelectPhotoTip'
import sensors from '../../../../utils/sensors'
import { selectSearchFilter } from '../../../../slices/photoFilterSlice'
import { selectPreciseSearch } from '../../../../slices/photosByPreciseSlice'
import {
  getSelectionConfig,
  normalizeToSet,
  PhotoSelectionSource,
  useActiveSelection,
  buildSuggestedPhotosQueryFilter
} from '../../utils/selectionHelpers'
import { buildFetchPhotosParamsWithOptions } from '../../lib/photo'

const maxBatchCount = 200

interface BatchRemoveWatermarkButtonProps {
  dataSource?: PhotoSelectionSource
  sensorReport?: () => void
}

/**
 * 批量去水印按钮组件（photov2）
 */
const BatchRemoveWatermarkButton: React.FC<BatchRemoveWatermarkButtonProps> = ({
  dataSource = 'content',
  sensorReport = () => {
    /* noop:默认不上报 */
  }
}) => {
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const groupId = useAppSelector((state) => state.group.groupId)
  const role = useSensorsRole()
  const queryFilter = useAppSelector(selectSearchFilter)
  const preciseSearch = useAppSelector(selectPreciseSearch)

  const {
    selectionConfig,
    hasSuggestedSelected,
    activePhotos: photos,
    activeCheckAll: checkAll,
    activeCheckedPhotos: checkedPhotos,
    activeUnCheckedPhotos: unCheckedPhotos,
    suggestedWords,
    suggestedQueryFilter
  } = useActiveSelection(dataSource)

  const [modalStatus, setModalStatus] = useState({
    visible: false,
    taskID: null as string | null,
    fileName: ''
  })

  const [total, setTotal] = useState(0)

  const dispatch = useAppDispatch()

  const srcUrls = useRef<string[]>()

  // 计算是否禁用按钮
  const disabled = useMemo(() => {
    return checkedPhotos.size === 0 && !checkAll
  }, [checkedPhotos.size, checkAll])

  // 计算排除的照片
  const exclude: string[] = useMemo(() => {
    if (checkAll) {
      return photos
        .filter((photo) => unCheckedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, unCheckedPhotos])

  // 计算包含的照片
  const include: string[] = useMemo(() => {
    if (!checkAll) {
      return photos
        .filter((photo) => checkedPhotos.has(photo.seq))
        .map((photo) => photo.file)
    }
    return []
  }, [checkAll, photos, checkedPhotos])

  const createRemoveWatermarkTask = () => {
    dispatch(
      createProducePhotoTask({
        userID: userId,
        groupID: groupId,
        srcUrls: srcUrls.current,
        type: 1,
        source: 'web_remove_watermark_batch'
      })
    ).then((res: any) => {
      setModalStatus({
        visible: true,
        taskID: res.taskID,
        fileName: ''
      })
    })
  }

  const handleBatchRemoveWatermark = async () => {
    sensorReport()

    if (!queryFilter || !groupId) {
      return
    }

    const isPreciseSearch = dataSource === 'precise'

    let currentQueryFilter = queryFilter
    if (hasSuggestedSelected && currentQueryFilter) {
      currentQueryFilter =
        buildSuggestedPhotosQueryFilter(
          queryFilter,
          suggestedQueryFilter,
          suggestedWords || []
        ) || currentQueryFilter
    }

    // 构建 filter 参数
    const baseParams = buildFetchPhotosParamsWithOptions({
      queryFilter: currentQueryFilter,
      groupID: groupId,
      lastPageCond: '',
      isPreciseSearch,
      excludeKeywords: isPreciseSearch ? preciseSearch?.notWords : undefined
    })

    // 添加 include 和 exclude
    if (checkAll) {
      baseParams.filter.exclude = exclude
    } else {
      baseParams.filter.include = include
    }

    const res = await dispatch(
      batchRemoveWatermarkCheck({
        filter: baseParams.filter,
        groupID: groupId,
        isPreciseSearch
      })
    )

    const { invalidUrls, urls, total, status } = res as any

    setTotal(total)

    if (status === -122) {
      return message.warning('任务过多，请稍后重试')
    }

    if (status === -125) {
      sensors.track('web_get_action_batch_removewm_tips', {
        actionType: 'overLimit',
        groupID: groupId,
        role,
        photoNum: total,
        photoNumError: invalidUrls.length
      })

      return message.warning(
        <span
          style={{
            fontWeight: 'bold'
          }}
        >{`当前选中${total}张照片，批量去水印最多支持${maxBatchCount}张照片`}</span>
      )
    }

    if (urls.length === 0) {
      sensors.track('web_get_action_batch_removewm_tips', {
        actionType: 'cannotRemove',
        groupID: groupId,
        role,
        photoNum: total,
        photoNumError: invalidUrls.length
      })

      return message.warning('所选照片均无法去水印，请重新选择')
    }

    srcUrls.current = urls

    if (invalidUrls.length === 0) {
      createRemoveWatermarkTask()
    } else {
      Modal.confirm({
        title: '检测到有不可去水印的照片',
        icon: <InfoCircleOutlined />,
        content: `本次共选择${total}张照片，检测到${invalidUrls.length}张不可去水印，点击继续将会跳过这${invalidUrls.length}张。`,
        okText: '继续去水印',
        onCancel: () => {
          sensors.track('web_get_action_batch_removewm_tips', {
            actionType: 'cannotRemovePartly',
            groupID: groupId,
            role,
            photoNum: total,
            photoNumError: invalidUrls.length,
            clickItem: 'cancel'
          })
        },
        onOk: () => {
          sensors.track('web_get_action_batch_removewm_tips', {
            actionType: 'cannotRemovePartly',
            groupID: groupId,
            role,
            photoNum: total,
            photoNumError: invalidUrls.length,
            clickItem: 'continueRemove'
          })

          createRemoveWatermarkTask()
        }
      })
    }
  }

  const Btn = () => {
    const btn = (
      <Button
        type="default"
        ghost={disabled}
        disabled={disabled}
        style={{ marginRight: 10 }}
        onClick={handleBatchRemoveWatermark}
      >
        去水印
      </Button>
    )

    return disabled ? <SelectPhotoTip>{btn}</SelectPhotoTip> : btn
  }

  return (
    <>
      <Btn />

      <RemoveWatermarkModal
        {...modalStatus}
        isBatch={true}
        photoNum={total}
        onRetry={createRemoveWatermarkTask}
        onCancel={() => {
          setModalStatus({
            visible: false,
            taskID: null,
            fileName: ''
          })
        }}
        dataSource={dataSource}
      />
    </>
  )
}

export default BatchRemoveWatermarkButton
